import { useMutation, useQuery } from '@apollo/client';
import { Button, Group, LoadingOverlay, Modal, ModalProps, Stack, Text } from '@mantine/core';
import { MAKE_CHECKOUT_SESSION } from 'graphql/mutations';
import { GET_ACTIVE_STRIPE_CONFIGS } from 'graphql/queries';
import { supabase } from 'config/supabase';
import { useHistory } from 'react-router';
import routes from 'config/routes';
import { useCourseConfig } from 'course-config';

interface Props extends Omit<ModalProps, 'onClose'> {}

/**
 * Shown when a user has a subscription record but it has lapsed (endDate in the past).
 * Access is now gated on an active subscription (backend entitlement enforcement), so a
 * lapsed user would otherwise see an empty, silently-broken app. Unlike NoSubscriptionsModal
 * they've already registered, so we don't re-collect their profile — just a clear message and
 * a Renew action that reuses the same Stripe checkout.
 */
export function ExpiredSubscriptionModal(props: Props) {
    const { data: stripeConfigs, loading: loadingConfigs } = useQuery(GET_ACTIVE_STRIPE_CONFIGS);
    const [makeCheckoutSession, { loading: loadingCheckout }] = useMutation(MAKE_CHECKOUT_SESSION);
    const history = useHistory();
    const { supportEmail } = useCourseConfig();

    const onRenew = async () => {
        const config = stripeConfigs?.activeStripeConfigs?.[0];
        if (!config) {
            alert(`Unable to process payments online - please contact us at ${supportEmail}`);
            return;
        }

        const res = await makeCheckoutSession({
            variables: { input: { priceId: config.priceId } }
        });
        const url = res.data?.makeCheckoutSession?.redirectUrl;
        if (!url) {
            alert('Something went wrong, please contact support.');
            return;
        }

        location.href = url;
    };

    const onLogout = async () => {
        await supabase.auth.signOut();
        history.push(routes.PUBLIC.SIGNUP_PAGE.url);
    };

    return (
        <Modal
            {...props}
            centered
            title="Your subscription has expired"
            onClose={() => {}}
            closeButtonProps={{ style: { display: 'none' } }}
        >
            <LoadingOverlay visible={loadingConfigs} />
            <Stack>
                <Text>
                    Your access to The Cardiac CT Course has expired. Renew your subscription to
                    continue your training and access your cases.
                </Text>
                <Text size="sm" c="dimmed">
                    Your progress and logbook are saved — renewing restores full access.
                </Text>
                <Group justify="space-between" mt="md">
                    <Button variant="subtle" onClick={onLogout} disabled={loadingCheckout}>
                        Log Out
                    </Button>
                    <Button onClick={onRenew} loading={loadingCheckout}>
                        Renew subscription
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
}
